import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, XCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function DonateSuccess() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ status: 'polling', data: null, error: null });
  const attemptsRef = useRef(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', data: null, error: 'Missing session_id' });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState({ status: 'timeout', data: null, error: 'Timed out waiting for confirmation.' });
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/donations/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setState({ status: 'paid', data, error: null });
          return;
        }
        if (data.status === 'expired') {
          setState({ status: 'expired', data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', data: null, error: err.response?.data?.detail || err.message });
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="bg-white min-h-screen flex items-center justify-center px-4 py-20" data-testid="donate-success-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-xl w-full border-2 border-black p-10 shadow-[8px_8px_0_0_rgba(0,0,0,1)]"
      >
        {state.status === 'polling' && (
          <div className="text-center" data-testid="donate-status-polling">
            <Loader2 size={56} className="animate-spin mx-auto text-accent mb-6" />
            <h1 className="font-unbounded font-black text-3xl mb-3">Confirming your donation…</h1>
            <p className="font-manrope text-gray-600">Hang tight — Stripe is finalizing your payment.</p>
          </div>
        )}
        {state.status === 'paid' && (
          <div className="text-center" data-testid="donate-status-paid">
            <CheckCircle2 size={56} className="mx-auto text-accent mb-6" />
            <h1 className="font-unbounded font-black text-3xl mb-3">Thank you!</h1>
            <p className="font-manrope text-gray-700 mb-2">
              Your <strong>${Number(state.data.amount).toFixed(2)}</strong> {state.data.frequency !== 'once' ? `${state.data.frequency} recurring ` : ''}donation has been received.
            </p>
            <p className="font-manrope text-gray-500 text-sm mb-8">A receipt has been emailed to you by Stripe.</p>
            <Link
              to="/"
              data-testid="back-home-link"
              className="inline-block bg-black text-white px-8 py-4 font-manrope font-bold uppercase tracking-wide hover:bg-accent transition-all"
            >
              Back to Home
            </Link>
          </div>
        )}
        {state.status === 'expired' && (
          <div className="text-center" data-testid="donate-status-expired">
            <XCircle size={56} className="mx-auto text-red-500 mb-6" />
            <h1 className="font-unbounded font-black text-3xl mb-3">Session expired</h1>
            <p className="font-manrope text-gray-600 mb-8">No charge was made. Feel free to try again.</p>
            <Link to="/donate" className="inline-block bg-black text-white px-8 py-4 font-manrope font-bold uppercase tracking-wide">
              Try again
            </Link>
          </div>
        )}
        {state.status === 'timeout' && (
          <div className="text-center" data-testid="donate-status-timeout">
            <Clock size={56} className="mx-auto text-yellow-500 mb-6" />
            <h1 className="font-unbounded font-black text-3xl mb-3">Still processing…</h1>
            <p className="font-manrope text-gray-600">Your donation may still go through. Check your email for a receipt from Stripe.</p>
          </div>
        )}
        {state.status === 'error' && (
          <div className="text-center" data-testid="donate-status-error">
            <XCircle size={56} className="mx-auto text-red-500 mb-6" />
            <h1 className="font-unbounded font-black text-3xl mb-3">Something went wrong</h1>
            <p className="font-manrope text-gray-600 mb-8">{state.error}</p>
            <Link to="/donate" className="inline-block bg-black text-white px-8 py-4 font-manrope font-bold uppercase tracking-wide">
              Try again
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
